/** @jsx jsx */
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { jsx } from '@balance-web/core';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { useForkedRef } from '@balance-web/utils';
import { useInputStyles } from '@balance-web/text-input';

export type AGGridCurrencyInputProps = {
  /**
   * These are the props you get from AG Grid when creating a custom cell editor.
   * You're supposed to pass them directly to this component.
   */
  agGridProps: CustomCellEditorProps<any, number | string | undefined>;
};

/**
 * We want all the changes in the CurrencyInput to be temporary.
 * The changes are committed to the consumer onChange only when user hits Enter, Tab, or clicks outside.
 * All other interactions (escape, blur etc) are considered a bailout and will cause the input to revert to the consumer value.
 */
export const AGGridCurrencyInput = forwardRef<
  HTMLInputElement,
  AGGridCurrencyInputProps
>(({ agGridProps }, ref) => {
  // @ts-ignore Sanitise invalid dom attributes
  const consumerProps = { ...agGridProps };
  if ('blur' in consumerProps && typeof consumerProps.blur === 'function') {
    delete consumerProps.blur;
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const composedRef = useForkedRef(inputRef, ref);

  const focusInput = useCallback((select?: boolean) => {
    if (inputRef.current) {
      if (select) {
        inputRef.current.select();
      } else {
        inputRef.current.focus();
      }
    }
  }, []);

  const commitValue = useCallback(
    (value: string | number) => {
      const numValue =
        typeof value === 'string' ? (value === '' ? null : value) : value;
      if (numValue !== agGridProps.value) {
        agGridProps.onValueChange(numValue);
      }
    },
    [agGridProps]
  );

  const handleChange = useCallback(
    (value: string | number) => {
      commitValue(value);
    },
    [commitValue]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        commitValue((e.target as HTMLInputElement).value);
        agGridProps.stopEditing();
      } else if (e.key === 'Escape') {
        agGridProps.stopEditing(true);
      } else if (e.key === 'Tab') {
        commitValue((e.target as HTMLInputElement).value);
        agGridProps.stopEditing();
      }
    },
    [agGridProps, commitValue]
  );

  const handleBlur = useCallback(() => {
    // Commit the value on blur
    if (inputRef.current) {
      commitValue(inputRef.current.value);
    }
    agGridProps.stopEditing();
  }, [agGridProps, commitValue]);

  const inputStyles = useInputStyles({
    shape: 'square',
    size: 'small',
  });

  useEffect(() => {
    focusInput(true);
  }, [focusInput]);

  return (
    <input
      ref={composedRef}
      css={{ ...inputStyles, border: 'none' }}
      type="text"
      value={agGridProps.value ?? ''}
      onBlur={handleBlur}
      onChange={(e) => {
        handleChange(e.target.value);
      }}
      onKeyDown={handleKeyDown}
    />
  );
});

AGGridCurrencyInput.displayName = 'AGGridCurrencyInput';
